import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type {
  PickerApplyResult,
  PickerCarrier,
  PickerData,
  PickerInitialSelection,
  PickerProduct,
} from './company-product-picker.types';

type CarrierSelState = 'none' | 'partial' | 'all';
type ColumnSide = 'left' | 'right';

/** Picker render mode.
 *
 *  · `both` — legacy two-pane miller (companies on the left + products on
 *    the right). Retained for any caller that wants the inline composite.
 *  · `companies-only` — just the companies column. Used by the rates +
 *    products rails as the inline body of the Carrier group. The host
 *    listens to (rowClick) to anchor an overlay fly-out per row.
 *  · `products-only` — just the products column for the currently focused
 *    carrier. Used by `CarrierProductFlyOutComponent` (the per-carrier
 *    fly-out anchored to the clicked rail row).
 */
export type PickerMode = 'both' | 'companies-only' | 'products-only';

/**
 * CompanyProductPicker — companies + products picker.
 *
 * 2026-06-03 PM refactor: the previous 500px-wide inline two-pane variant
 * was too wide. Split into two views via a `mode` input:
 *   · `companies-only` renders inside the rail's Carrier group (rail is
 *     back to 280px). Clicking a carrier ROW (not the checkbox) emits
 *     (rowClick) so the caller can open a CDK Overlay fly-out anchored
 *     to that row. Clicking the CHECKBOX still toggles the whole carrier
 *     (no fly-out needed).
 *   · `products-only` renders inside that fly-out and shows the focused
 *     carrier's products. Inherits the same search query (passed via
 *     [externalSearchQuery]) so the page-header input filters the fly-out
 *     too.
 *   · `both` (default) keeps the legacy two-pane layout for any caller
 *     not yet migrated. No callers use this mode after this refactor.
 *
 * 2026-06-04 search-consolidation: the picker no longer renders its own
 * search input. All three previous text inputs (rail-top + in-picker on
 * both rates and products) are replaced by ONE page-header input per
 * surface. The picker's filtering is driven entirely by the
 * `externalSearchQuery` Input (the rates/products page binds it to
 * filterState.searchText). When the input is empty the picker shows
 * everything — there is no internal fallback query any more.
 *
 * Live-applies via (selectionChange). No Apply/Cancel staging.
 */
@Component({
  selector: 'app-company-product-picker',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './company-product-picker.component.html',
  styleUrl: './company-product-picker.component.scss',
})
export class CompanyProductPickerComponent implements OnInit {
  // ── Internal signals (declared FIRST so @Input setters can write them) ─
  private readonly _data = signal<PickerData>({ carriers: [] });
  private readonly _selectedProductIds = signal<Set<string>>(new Set());
  private readonly _externalSearchQuery = signal<string>('');
  protected readonly focusedCarrierId = signal<string | null>(null);
  protected readonly activeColumn = signal<ColumnSide>('left');
  protected readonly activeIndex = signal<number>(0);

  /** Readonly signal accessors for template / derived computeds. */
  protected readonly data = this._data.asReadonly();
  protected readonly selectedProductIds = this._selectedProductIds.asReadonly();

  /** Render mode — see PickerMode jsdoc on the type. */
  @Input() mode: PickerMode = 'both';

  /** External search query. Drives visibleCarriers / visibleProductsOfFocused.
   *  As of 2026-06-04 search-consolidation this is the SOLE source of the
   *  picker's text filter — the picker no longer renders its own input.
   *  Both rails bind this to filterState.searchText so the same page-header
   *  input filters the grid AND the picker. */
  @Input()
  set externalSearchQuery(v: string | null | undefined) {
    this._externalSearchQuery.set(v ?? '');
  }

  /** Externally-driven focused carrier id. Used by the fly-out so the host
   *  controls which carrier's products are shown. Null falls back to the
   *  picker's internal focus tracking (first carrier on mount). */
  @Input()
  set focusedCarrierIdInput(v: string | null | undefined) {
    if (v !== undefined) this.focusedCarrierId.set(v ?? null);
  }

  /** Full carrier+product tree the user picks from. */
  @Input({ required: true })
  set dataInput(v: PickerData) {
    this._data.set(v ?? { carriers: [] });
    // If the data changed and the previously-focused carrier no longer
    // exists (typical on tab switch), reseat focus to the first carrier
    // so the right pane has something to show.
    // Only CLEAR a focus the new data no longer contains. Seeding a default
    // moved to ngOnInit, which runs after every input is set -- in
    // companies-only mode the focus is an accordion expansion, and defaulting
    // it here left the first carrier hanging open on load.
    const focused = this.focusedCarrierId();
    if (focused && !this._data().carriers.some((c) => c.id === focused)) {
      this.focusedCarrierId.set(null);
    }
  }

  /** Pre-checked products at open time. */
  @Input()
  set initialSelection(v: PickerInitialSelection | null | undefined) {
    if (v?.selectedProductIds?.length) {
      this._selectedProductIds.set(new Set(v.selectedProductIds));
    } else {
      this._selectedProductIds.set(new Set());
    }
  }

  /** Live-emits on every selection change. Callers write straight to
   *  their filter state — there is no Apply step. */
  @Output() selectionChange = new EventEmitter<PickerApplyResult>();

  /** Fires when the body of a carrier row (NOT the checkbox) is clicked.
   *  Used by `companies-only` mode to let the host anchor a fly-out to
   *  the clicked row. The event carries both the carrier id and the row's
   *  DOM element for FlexibleConnectedPositionStrategy. */
  @Output() rowClick = new EventEmitter<{ carrierId: string; el: HTMLElement }>();

  // ── Derivations ──────────────────────────────────────────────────
  protected readonly carriers = computed<PickerCarrier[]>(() => this._data().carriers);

  /** Effective search query — sourced exclusively from the externalSearchQuery
   *  Input (2026-06-04 search-consolidation). When empty the picker shows all
   *  rows. */
  protected readonly searchQuery = computed<string>(() => this._externalSearchQuery());

  protected readonly visibleCarriers = computed<PickerCarrier[]>(() => {
    const q = this.searchQuery().trim().toLowerCase();
    if (!q) return this.carriers();
    return this.carriers().filter((c) => {
      if (c.name.toLowerCase().includes(q)) return true;
      return c.products.some((p) => p.name.toLowerCase().includes(q));
    });
  });

  protected readonly focusedCarrier = computed<PickerCarrier | null>(() => {
    const id = this.focusedCarrierId();
    if (!id) return null;
    return this.carriers().find((c) => c.id === id) ?? null;
  });

  protected readonly visibleProductsOfFocused = computed<PickerProduct[]>(() => {
    const fc = this.focusedCarrier();
    if (!fc) return [];
    const q = this.searchQuery().trim().toLowerCase();
    if (!q) return fc.products;
    // Show all of carrier's products if carrier name matches; otherwise filter
    if (fc.name.toLowerCase().includes(q)) return fc.products;
    return fc.products.filter((p) => p.name.toLowerCase().includes(q));
  });

  protected readonly selectedCarrierCount = computed<number>(() => {
    const sel = this.selectedProductIds();
    return this.carriers().filter((c) => c.products.some((p) => sel.has(p.id))).length;
  });

  protected readonly selectAllLabel = computed<string>(() => {
    const visible = this.visibleProductsOfFocused();
    const sel = this.selectedProductIds();
    const allChecked = visible.length > 0 && visible.every((p) => sel.has(p.id));
    if (allChecked) return 'Unselect all';
    const fc = this.focusedCarrier();
    const total = fc?.products.length ?? visible.length;
    return `Select all ${total}`;
  });

  protected carrierSelectionState(carrierId: string): CarrierSelState {
    const c = this.carriers().find((x) => x.id === carrierId);
    if (!c || c.products.length === 0) return 'none';
    const sel = this.selectedProductIds();
    const selCount = c.products.filter((p) => sel.has(p.id)).length;
    if (selCount === 0) return 'none';
    if (selCount === c.products.length) return 'all';
    return 'partial';
  }

  /** Number of selected products under a carrier — used by the rail row's
   *  count chip ("3/5"). */
  protected carrierSelectedCount(carrierId: string): number {
    const c = this.carriers().find((x) => x.id === carrierId);
    if (!c) return 0;
    const sel = this.selectedProductIds();
    return c.products.filter((p) => sel.has(p.id)).length;
  }

  protected isProductSelected(id: string): boolean {
    return this.selectedProductIds().has(id);
  }

  protected logoClassFor(c: PickerCarrier): string {
    if (c.logoClass) return c.logoClass;
    // Deterministic fallback: hash the carrier name to one of the 12
    // gradients defined in the SCSS (`logo-1` … `logo-12`).
    let h = 0;
    for (let i = 0; i < c.name.length; i++) {
      h = (h * 31 + c.name.charCodeAt(i)) >>> 0;
    }
    return `logo-${(h % 12) + 1}`;
  }

  /** Highlight the search match using <mark> inside an [innerHTML] binding. */
  protected highlight(text: string): string {
    const q = this.searchQuery().trim();
    if (!q) return this.escapeHtml(text);
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return this.escapeHtml(text);
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + q.length);
    const after = text.slice(idx + q.length);
    return `${this.escapeHtml(before)}<mark>${this.escapeHtml(match)}</mark>${this.escapeHtml(after)}`;
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Lifecycle ────────────────────────────────────────────────────
  ngOnInit(): void {
    // Seed focused carrier from initial selection if any, else first carrier.
    //
    // NOT in companies-only mode: there the focus is which carrier is expanded
    // in the accordion, and the list has to open with everything closed.
    if (this.mode !== 'companies-only' && !this.focusedCarrierId()) {
      const sel = this.selectedProductIds();
      let initial: string | null = null;
      if (sel.size > 0) {
        // Pick the first carrier that has a selected product.
        for (const c of this.carriers()) {
          if (c.products.some((p) => sel.has(p.id))) {
            initial = c.id;
            break;
          }
        }
      }
      initial = initial ?? this.carriers()[0]?.id ?? null;
      this.focusedCarrierId.set(initial);
    }
  }

  // ── Event handlers ───────────────────────────────────────────────
  /** Click on the body of a carrier row (NOT the checkbox).
   *  - In `companies-only` mode, emits (rowClick) so the host can open
   *    a fly-out anchored to that row. The element is needed for
   *    FlexibleConnectedPositionStrategy.
   *  - In `both` mode, sets the focused carrier (right pane updates). */
  onCarrierRowClick(id: string, el: HTMLElement): void {
    // companies-only is an accordion: clicking the open carrier closes it.
    this.focusedCarrierId.set(
      this.mode === 'companies-only' && this.focusedCarrierId() === id ? null : id,
    );
    this.activeColumn.set('left');
    const idx = this.visibleCarriers().findIndex((c) => c.id === id);
    this.activeIndex.set(Math.max(0, idx));
    if (this.mode === 'companies-only') {
      this.rowClick.emit({ carrierId: id, el });
    }
  }

  onToggleCarrier(id: string, ev: Event): void {
    ev.stopPropagation();
    const c = this.carriers().find((x) => x.id === id);
    if (!c) return;
    const sel = new Set(this.selectedProductIds());
    const state = this.carrierSelectionState(id);
    if (state === 'all') {
      for (const p of c.products) sel.delete(p.id);
    } else {
      for (const p of c.products) sel.add(p.id);
    }
    this._selectedProductIds.set(sel);
    this.emitSelectionChange();
  }

  onToggleProduct(id: string): void {
    const sel = new Set(this.selectedProductIds());
    if (sel.has(id)) sel.delete(id);
    else sel.add(id);
    this._selectedProductIds.set(sel);
    this.emitSelectionChange();
  }

  onSelectAllVisible(): void {
    const fc = this.focusedCarrier();
    if (!fc) return;
    const visible = this.visibleProductsOfFocused();
    const sel = new Set(this.selectedProductIds());
    const allChecked = visible.length > 0 && visible.every((p) => sel.has(p.id));
    if (allChecked) {
      for (const p of visible) sel.delete(p.id);
    } else {
      for (const p of visible) sel.add(p.id);
    }
    this._selectedProductIds.set(sel);
    this.emitSelectionChange();
  }

  private emitSelectionChange(): void {
    this.selectionChange.emit(this.buildResult());
  }

  private buildResult(): PickerApplyResult {
    const selProducts = Array.from(this.selectedProductIds());
    const selCarriers = this.carriers()
      .filter((c) => c.products.some((p) => this.selectedProductIds().has(p.id)))
      .map((c) => c.id);
    return {
      selectedProductIds: selProducts,
      selectedCarrierIds: selCarriers,
    };
  }

  // ── Keyboard ─────────────────────────────────────────────────────
  //
  // 2026-06-04 search-consolidation: the picker no longer renders its own
  // search input, so the Ctrl+F focus and Esc-to-clear keyboard paths were
  // dropped. Arrow keys + Space still drive carrier/product navigation, but
  // only when focus is already inside the picker's DOM subtree.
  @HostListener('document:keydown', ['$event'])
  onKeyDown(ev: KeyboardEvent): void {
    const active = (typeof document !== 'undefined') ? document.activeElement : null;
    const hostEl = (active as HTMLElement | null)?.closest('.picker') ?? null;
    const focusInside = !!hostEl;

    // Arrow keys + Space — only when focus is inside the picker.
    if (!focusInside) return;

    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      const list = this.activeColumn() === 'left' ? this.visibleCarriers() : this.visibleProductsOfFocused();
      if (list.length === 0) return;
      const dir = ev.key === 'ArrowDown' ? 1 : -1;
      const next = Math.max(0, Math.min(list.length - 1, this.activeIndex() + dir));
      this.activeIndex.set(next);
      if (this.activeColumn() === 'left') {
        const id = (list as PickerCarrier[])[next]?.id;
        if (id) this.focusedCarrierId.set(id);
      }
    } else if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      this.activeColumn.set('right');
      this.activeIndex.set(0);
    } else if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      this.activeColumn.set('left');
      const list = this.visibleCarriers();
      const idx = Math.max(0, list.findIndex((c) => c.id === this.focusedCarrierId()));
      this.activeIndex.set(idx);
    } else if (ev.key === ' ' || ev.code === 'Space') {
      ev.preventDefault();
      if (this.activeColumn() === 'left') {
        const list = this.visibleCarriers();
        const target = list[this.activeIndex()];
        if (target) {
          // Reuse toggle path. Build a synthetic event so the click handler can stopPropagation it.
          const fakeEv = { stopPropagation: () => {} } as Event;
          this.onToggleCarrier(target.id, fakeEv);
        }
      } else {
        const list = this.visibleProductsOfFocused();
        const target = list[this.activeIndex()];
        if (target) this.onToggleProduct(target.id);
      }
    }
  }
}
